import React, {
    createContext,
    useCallback,
    useContext,
    useReducer,
    ReactNode,
} from 'react';

declare enum NotificationTypeEnum {
    info = 'info',
    success = 'success',
    warning = 'warning',
    alert = 'alert',
    neutral = 'neutral',
    brand = 'brand',
}
export type NotificationType = keyof typeof NotificationTypeEnum;

type State = {
    title: string;
    open: boolean;
    message: string;
    severity: NotificationType;
    path: string | null;
};

type Action =
    | { type: 'OPEN_ALERT'; payload: Partial<State> }
    | { type: 'CLOSE_ALERT' };

const AlertContext = createContext<
    | {
          state: State;
          openAlert: (payload: Partial<State>) => void;
          closeAlert: () => void;
      }
    | undefined
>(undefined);

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

const initialState: State = {
    title: '',
    open: false,
    message: '',
    severity: 'info',
    path: null,
};

const alertReducer = (state: State, action: Action): State => {
    switch (action?.type) {
        case 'OPEN_ALERT':
            return { ...state, open: true, ...action.payload };
        case 'CLOSE_ALERT':
            return { ...state, open: false };
        default:
            return state;
    }
};

export const AlertProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(alertReducer, initialState);

    const openAlert = useCallback((payload: Partial<State>) => {
        dispatch({ type: 'OPEN_ALERT', payload });
    }, []);

    const closeAlert = useCallback(() => {
        dispatch({ type: 'CLOSE_ALERT' });
    }, []);

    return (
        <AlertContext.Provider value={{ state, openAlert, closeAlert }}>
            {children}
        </AlertContext.Provider>
    );
};
